import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ModuleGuard from "@/components/ModuleGuard";
import HubPage from "@/pages/HubPage";
import LoginPage from "@/pages/LoginPage";
import AuthCallback from "@/pages/AuthCallback";
import AdminGuard from "@/components/AdminGuard";
import AdminPage from "@/pages/AdminPage";

// Team Room (unified HR + Petty Cash)
import TeamRoomLayout from "@/components/TeamRoomLayout";
import DashboardPage from "@/pages/DashboardPage";
import EmployeesPage from "@/pages/EmployeesPage";
import EmployeeDetailPage from "@/pages/EmployeeDetailPage";
import AttendancePage from "@/pages/AttendancePage";
import ReportsPage from "@/pages/ReportsPage";
import PettyCashDashboardPage from "@/pages/PettyCashDashboardPage";
import PettyCashTransactionsPage from "@/pages/PettyCashTransactionsPage";
import PettyCashReportsPage from "@/pages/PettyCashReportsPage";

// Quotes
import QuotesLayout from "@/components/QuotesLayout";
import QuotesListPage from "@/pages/QuotesListPage";
import QuoteFormPage from "@/pages/QuoteFormPage";
import QuoteDetailPage from "@/pages/QuoteDetailPage";
import QuotesDashboardPage from "@/pages/QuotesDashboardPage";

import { Toaster } from "@/components/ui/sonner";

/**
 * If the browser lands anywhere in the app with `#session_id=...` in the URL
 * (Emergent OAuth's default return), route it to the correct module's
 * AuthCallback based on the current pathname.
 */
function HashSessionRouter() {
  const location = useLocation();
  const p = location.pathname;
  const module = p.startsWith("/quotes")
    ? "quotes"
    : p.startsWith("/petty-cash")
      ? "petty-cash"
      : p.startsWith("/presently")
        ? "presently"
        : "team-room";
  return <AuthCallback module={module} />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <HashSessionRouter />;
  }
  return (
    <Routes>
      {/* Public module chooser */}
      <Route path="/" element={<HubPage />} />

      {/* Admin console (admin only) */}
      <Route
        path="/admin"
        element={
          <AdminGuard>
            <AdminPage />
          </AdminGuard>
        }
      />

      {/* Legacy: old shared /login → send to chooser */}
      <Route path="/login" element={<Navigate to="/" replace />} />

      {/* Team Room login (the new unified entry point) */}
      <Route path="/workspace/login" element={<LoginPage module="team-room" />} />
      <Route path="/workspace/auth/callback" element={<AuthCallback module="team-room" />} />

      {/* Team Room — HR attendance URLs */}
      <Route path="/presently/login" element={<Navigate to="/workspace/login" replace />} />
      <Route path="/presently/auth/callback" element={<AuthCallback module="presently" />} />
      <Route
        path="/presently"
        element={
          <ModuleGuard module="presently">
            <TeamRoomLayout />
          </ModuleGuard>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="employees" element={<EmployeesPage />} />
        <Route path="employees/:id" element={<EmployeeDetailPage />} />
        <Route path="attendance" element={<AttendancePage />} />
        <Route path="reports" element={<ReportsPage />} />
      </Route>

      {/* Team Room — Petty Cash URLs */}
      <Route path="/petty-cash/login" element={<Navigate to="/workspace/login" replace />} />
      <Route path="/petty-cash/auth/callback" element={<AuthCallback module="petty-cash" />} />
      <Route
        path="/petty-cash"
        element={
          <ModuleGuard module="petty-cash">
            <TeamRoomLayout />
          </ModuleGuard>
        }
      >
        <Route index element={<PettyCashDashboardPage />} />
        <Route path="transactions" element={<PettyCashTransactionsPage />} />
        <Route path="reports" element={<PettyCashReportsPage />} />
      </Route>

      {/* Quotes */}
      <Route path="/quotes/login" element={<LoginPage module="quotes" />} />
      <Route path="/quotes/auth/callback" element={<AuthCallback module="quotes" />} />
      <Route
        path="/quotes"
        element={
          <ModuleGuard module="quotes">
            <QuotesLayout />
          </ModuleGuard>
        }
      >
        <Route index element={<QuotesListPage />} />
        <Route path="new" element={<QuoteFormPage />} />
        <Route path="dashboard" element={<QuotesDashboardPage />} />
        <Route path=":id" element={<QuoteDetailPage />} />
        <Route path=":id/edit" element={<QuoteFormPage />} />
      </Route>

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster richColors position="top-right" />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
